import React, { ForwardRefRenderFunction, forwardRef } from "react";
import { useSearchParams } from "react-router-dom";
//#region redux
import { useSelector } from "react-redux";
import type { AppState } from "store";
//#endregion
//#region types
import { SettingsFormTypes } from "models/formTypes";
import { EditableTableMoreActionMenuItemType, FormHandles } from "models/propTypes/genericFormProps";
import { BillogramFormContainerProps } from "models/propTypes/integrationsPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { StyleEnums } from "models/enums";
import { FormikProps } from "formik";
import { AppCategory } from "models/enums/apps";
import type { StripeConnectStatusResponse } from "serviceHooks/useStripeConnectService";
import type { VismaAdvisorStatusResponse } from "serviceHooks/useVismaAdvisorService";
//#endregion
//#region components
import IntegrationSettingsForm from "./integrationSettingsForm";
import { Skeletons, useMountedState, useFloatingMessages } from "components/common";
import CommonIntegrationContainer from "components/integration/commonIntegrationContainer";
import { useSettingsService, useStripeConnectService, useVismaAdvisorService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import QBISLogo from "images/QBIS.Logo_.Vector.qbis_.blue_.png";
import BillogramLogo from "images/billogram_full_logo.png";
import JobbileLogo from "images/jobbile_logo_grey.png";
import StripeWordmark from "images/stripe_wordmark_slate.svg";
import { LocalizationHelperInstance } from "utils/helpers";

type StripeConnectStatus = StripeConnectStatusResponse & {
  statusUnavailable?: boolean;
};

const stripeStatusFallbackDelayMs = 2500;
const stripeStatusUnavailable = { statusUnavailable: true } as StripeConnectStatus;

// Row chip per connection status; undefined means the default "Aktiv".
const vismaAdvisorStatusLabel = (status: VismaAdvisorStatusResponse) => {
  switch (status.status) {
    case undefined:
      return TranslationKeys.Integrations.NotConnected;
    case "PendingConfirmation":
      return TranslationKeys.Integrations.VismaAdvisorConfirmAgency;
    case "NeedsReauthorization":
      return TranslationKeys.Integrations.VismaAdvisorLoginAgain;
    case "Paused":
      return TranslationKeys.Integrations.VismaAdvisorPaused;
    default:
      return undefined;
  }
};

// Callback results (?vismaAdvisor=...) other than "connected"; unknown codes get the generic text.
const vismaAdvisorErrorMessages: Record<string, string> = {
  login_cancelled: TranslationKeys.Integrations.VismaAdvisorLoginCancelled,
  "Connection.WrongAgency": TranslationKeys.Integrations.VismaAdvisorWrongAgency,
  "Connection.AgencyAlreadyConnected": TranslationKeys.Integrations.VismaAdvisorAgencyAlreadyConnected,
  "Connection.AuthorizationExpired": TranslationKeys.Integrations.VismaAdvisorLoginExpired,
};

const getVismaAdvisorLoginUrl = (authorizeUrl?: string) => {
  if (!authorizeUrl) return undefined;
  try {
    const parsedUrl = new URL(authorizeUrl);
    // Same defense-in-depth as Stripe: only ever navigate to Visma's identity host.
    return parsedUrl.protocol === "https:" && parsedUrl.hostname === "identity.vismaonline.com"
      ? parsedUrl.toString()
      : undefined;
  } catch {
    return undefined;
  }
};

const IntegrationSettingsFormContainer: ForwardRefRenderFunction<FormHandles, BillogramFormContainerProps> = (
  props,
  ref
) => {
  const settingsService = useSettingsService();
  const stripeConnectService = useStripeConnectService();
  const vismaAdvisorService = useVismaAdvisorService();
  const [vismaAdvisorStatus, setVismaAdvisorStatus] = React.useState<VismaAdvisorStatusResponse>({});
  const [loading, setLoading] = React.useState<boolean>(true);
  const [stripeConnectStatus, setStripeConnectStatus] = React.useState<StripeConnectStatus>({});
  const [isInitiatingStripeConnect, setIsInitiatingStripeConnect] = React.useState<boolean>(false);
  const [apps, setApps] = React.useState<WintSuperkollAPIModel.AppList>();
  const { getMounted } = useMountedState();
  const { enqueueSuccessMessage, enqueueErrorMessage } = useFloatingMessages();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedCompanyId = useSelector((state: AppState) => state.auth.selectedCompany?.id);

  const getStaticApps = () => {
    const staticApps = [] as SettingsFormTypes.IntegrationItem[];
    apps?.billogram &&
      staticApps.push({
        name: "Billogram",
        appCategory: AppCategory.Billogram,
        imageSource: BillogramLogo,
        isActive: apps?.billogram?.isActive,
        appData: apps?.billogram,
        activationState: apps?.billogram?.activationState,
      });
    apps?.qbis &&
      staticApps.push({
        name: "Qbis",
        appCategory: AppCategory.Qbis,
        imageSource: QBISLogo,
        isActive: apps?.qbis?.isActive,
        appData: apps?.qbis,
      });
    apps?.jobbile &&
      staticApps.push({
        name: "Jobbile",
        appCategory: AppCategory.Jobbile,
        imageSource: JobbileLogo,
        isActive: apps?.jobbile?.isActive,
        appData: apps?.jobbile,
      });
    // Only surface Stripe to companies that are actually enrolled or connected.
    // When the status call fails (e.g. the Stripe backend is not available in an
    // environment), we deliberately do NOT show the row so the integrations page
    // stays clean for every other customer instead of showing an "unavailable" row.
    const shouldShowStripeConnect = stripeConnectStatus.isEnrolled === true || stripeConnectStatus.isConnected === true;

    shouldShowStripeConnect &&
      staticApps.push({
        name: "Stripe",
        appCategory: AppCategory.Stripe,
        imageSource: StripeWordmark,
        isActive: stripeConnectStatus.isConnected === true,
        stripeConnectAccountId: stripeConnectStatus.accountId,
        displayOrder: 50,
        statusLabel: stripeConnectStatus.statusUnavailable
          ? TranslationKeys.Integrations.StripeStatusUnavailable
          : stripeConnectStatus.isConnected
            ? undefined
            : TranslationKeys.Integrations.NotConnected,
        statusDisplayType: stripeConnectStatus.isConnected ? undefined : StyleEnums.DisplayType.Warning,
        statusUnavailable: stripeConnectStatus.statusUnavailable,
      });

    // Shown only once Wint has switched Visma Advisor on for the company in Heart.
    (vismaAdvisorStatus.isEnrolled === true || vismaAdvisorStatus.isConnected === true) &&
      staticApps.push({
        name: "Visma Advisor",
        appCategory: AppCategory.VismaAdvisor,
        isActive: vismaAdvisorStatus.status === "Active",
        displayOrder: 60,
        statusLabel: vismaAdvisorStatusLabel(vismaAdvisorStatus),
        statusDisplayType:
          vismaAdvisorStatus.status === "Active"
            ? undefined
            : vismaAdvisorStatus.status === "NeedsReauthorization"
              ? StyleEnums.DisplayType.Danger
              : StyleEnums.DisplayType.Warning,
        vismaAdvisorStatus: vismaAdvisorStatus.status,
        vismaAdvisorAgencyName: vismaAdvisorStatus.agencyName,
      });

    return staticApps;
  };

  const dynamicApps =
    apps && apps.customAppList
      ? apps.customAppList?.map((c, index) => {
          return {
            name: c.name,
            description: c.description,
            appCategory: AppCategory.Default,
            isActive: c.isActive,
            appData: c,
            displayOrder: 1000 + index,
          } as SettingsFormTypes.IntegrationItem;
        })
      : [];

  const sortIntegrationItems = (items: SettingsFormTypes.IntegrationItem[]) =>
    items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const orderDiff = (a.item.displayOrder ?? 0) - (b.item.displayOrder ?? 0);
        return orderDiff !== 0 ? orderDiff : a.index - b.index;
      })
      .map(({ item }) => item);

  const validationSchema = Yup.object<SettingsFormTypes.IntegrationSettings>({
    canSave: Yup.boolean().required().oneOf([true]),
    apps: Yup.array().of(
      Yup.object<SettingsFormTypes.IntegrationItem>({
        appCategory: Yup.number(),
        appCategoryName: Yup.string(),
        isActive: Yup.boolean(),
        stripeConnectAccountId: Yup.string(),
        vismaAdvisorStatus: Yup.string(),
        vismaAdvisorAgencyName: Yup.string(),
      })
    ),
  });
  const defaultValues = {
    canSave: false,
    apps: sortIntegrationItems([...getStaticApps(), ...dynamicApps]),
  } as SettingsFormTypes.IntegrationSettings;

  React.useEffect(() => {
    if (!selectedCompanyId) return;

    let isCurrentRequest = true;
    let realStripeStatusApplied = false; // a successful getStatus result — final
    let anyStripeStatusApplied = false; // a provisional result — the fallback timer or a failed getStatus
    setLoading(true);
    setStripeConnectStatus({});
    setVismaAdvisorStatus({});

    // We never wait for Stripe — the page renders as soon as integration settings load. A successful
    // getStatus is final and may override an earlier fallback, so enrolled/connected companies still
    // surface Stripe on slow networks. Provisional outcomes (the timeout fallback or a failed getStatus)
    // apply at most once and never overwrite a real status, avoiding repeated status churn.
    const applyStripeStatus = (status: StripeConnectStatus, isReal: boolean) => {
      if (!getMounted() || !isCurrentRequest) return;
      if (realStripeStatusApplied) return;
      if (anyStripeStatusApplied && !isReal) return;
      anyStripeStatusApplied = true;
      if (isReal) realStripeStatusApplied = true;
      setStripeConnectStatus(status);
    };

    // Stop the skeleton on success or failure (showErrors: true already toasts), so a failed
    // integration-settings load can never leave the page stuck on the skeleton.
    settingsService
      .getIntegrationSettings()
      .then((res) => {
        if (getMounted() && isCurrentRequest) {
          setApps(res);
        }
      })
      .finally(() => {
        if (getMounted() && isCurrentRequest) {
          setLoading(false);
        }
      });

    const stripeStatusFallbackTimeout = window.setTimeout(
      () => applyStripeStatus(stripeStatusUnavailable, false),
      stripeStatusFallbackDelayMs
    );

    // Hidden unless the status call succeeds, so the page stays clean where the service is absent.
    vismaAdvisorService
      .getStatus()
      .then((status) => getMounted() && isCurrentRequest && setVismaAdvisorStatus(status ?? {}))
      .catch(() => undefined);

    stripeConnectService
      .getStatus()
      .then((status) => applyStripeStatus(status ?? {}, true))
      .catch(() => applyStripeStatus(stripeStatusUnavailable, false))
      .finally(() => window.clearTimeout(stripeStatusFallbackTimeout));

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(stripeStatusFallbackTimeout);
    };
  }, [selectedCompanyId]);

  // After the same-tab Stripe OAuth flow returns, the callback redirects back here with
  // ?success=true (and an accountId). Confirm the connection to the user, then strip the
  // one-off params from the address bar so a refresh or share link doesn't re-trigger it.
  React.useEffect(() => {
    if (searchParams.get("success") !== "true") return;

    enqueueSuccessMessage(
      LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Integrations.StripeConnectSuccess)
    );

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("success");
    nextParams.delete("accountId");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The Visma login callback returns here with ?vismaAdvisor=connected or an error code.
  React.useEffect(() => {
    const result = searchParams.get("vismaAdvisor");
    if (!result) return;

    if (result === "connected") {
      enqueueSuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Integrations.VismaAdvisorLoginSucceeded)
      );
    } else {
      enqueueErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(
          vismaAdvisorErrorMessages[result] ?? TranslationKeys.Integrations.VismaAdvisorLoginFailed
        )
      );
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("vismaAdvisor");
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onVismaAdvisorLoginClick = async () => {
    const { authorizeUrl } = await vismaAdvisorService.initiate(`${window.location.pathname}${window.location.search}`);
    const loginUrl = getVismaAdvisorLoginUrl(authorizeUrl);
    if (!loginUrl) {
      // This failure never passes through the API client, so nothing else would tell the user.
      enqueueErrorMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Integrations.VismaAdvisorLoginFailed)
      );
      throw new Error("Visma Advisor returned a missing or unexpected login URL.");
    }
    window.location.assign(loginUrl);
  };

  const onVismaAdvisorConfirmClick = async (isRightAgency: boolean) => {
    if (isRightAgency) {
      await vismaAdvisorService.confirm();
      enqueueSuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Integrations.VismaAdvisorConfirmed)
      );
    } else {
      await vismaAdvisorService.discard();
      enqueueSuccessMessage(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Integrations.VismaAdvisorDiscarded)
      );
    }

    // The action succeeded, so show its result now; a failed status reload must not turn it into an error.
    if (getMounted()) {
      setVismaAdvisorStatus((current) =>
        isRightAgency ? { ...current, status: "Active" } : { isEnrolled: current.isEnrolled }
      );
    }
    vismaAdvisorService
      .getStatus()
      .then((status) => getMounted() && status && setVismaAdvisorStatus(status))
      .catch(() => undefined);
  };

  const getStripeOAuthUrl = (response: Awaited<ReturnType<typeof stripeConnectService.initiateConnect>>) => {
    // OAuthUrl currently serializes as oAuthUrl from the C# DTO; keep oauthUrl as a fallback if the API normalizes it.
    const oauthUrl = response.oauthUrl ?? response.oAuthUrl;
    if (!oauthUrl) {
      throw new Error("Stripe connect did not return an OAuth URL.");
    }

    const parsedUrl = new URL(oauthUrl);
    if (parsedUrl.protocol !== "https:") {
      throw new Error("Stripe connect returned an invalid OAuth URL.");
    }

    // Defense-in-depth against an open redirect: only ever navigate to a Stripe host, even though
    // the URL comes from our own backend.
    const isStripeHost = parsedUrl.hostname === "connect.stripe.com" || parsedUrl.hostname.endsWith(".stripe.com");
    if (!isStripeHost) {
      throw new Error("Stripe connect returned an unexpected OAuth host.");
    }

    return parsedUrl.toString();
  };

  const onStripeConnectClick = async () => {
    if (isInitiatingStripeConnect) return;

    setIsInitiatingStripeConnect(true);

    try {
      const response = await stripeConnectService.initiateConnect(
        `${window.location.pathname}${window.location.search}`
      );
      const oauthUrl = getStripeOAuthUrl(response);

      // Navigate in the same tab; the callback redirects back to the integrations page
      // with ?success=true, which the effect above turns into a confirmation toast.
      window.location.assign(oauthUrl);
    } catch (error) {
      // The API client shows the user-facing error toast when showErrors is true.
      if (getMounted()) {
        setIsInitiatingStripeConnect(false);
      }
      throw error;
    }
  };

  const onStripeDisconnectClick = async (accountId: string) => {
    await stripeConnectService.disconnect(accountId);

    if (getMounted()) {
      setStripeConnectStatus((currentStatus) => ({
        ...currentStatus,
        accountId: undefined,
        accountStatus: undefined,
        isConnected: false,
        syncMode: "notConnected",
      }));
    }
  };

  const onAddOrEditApp = (value: any) => {
    return (
      <CommonIntegrationContainer
        onStripeConnectClick={onStripeConnectClick}
        onStripeDisconnectClick={onStripeDisconnectClick}
        onVismaAdvisorLoginClick={onVismaAdvisorLoginClick}
        onVismaAdvisorConfirmClick={onVismaAdvisorConfirmClick}
        submitResultFormatter={(appItem: SettingsFormTypes.IntegrationItem): SettingsFormTypes.IntegrationItem => {
          return {
            appCategory: appItem.appCategory,
            imageSource: appItem.imageSource,
            isActive: appItem.isActive,
            description: appItem.description,
            name: appItem.name,
            appData: appItem.appData,
            activationState: appItem.activationState,
            displayOrder: appItem.displayOrder,
            stripeConnectAccountId: appItem.stripeConnectAccountId,
            statusLabel: appItem.statusLabel,
            statusDisplayType: appItem.statusDisplayType,
            statusUnavailable: appItem.statusUnavailable,
            vismaAdvisorStatus: appItem.vismaAdvisorStatus,
            vismaAdvisorAgencyName: appItem.vismaAdvisorAgencyName,
          };
        }}
      ></CommonIntegrationContainer>
    );
  };

  const onCustomAppRemove = async (
    rowIndex: number,
    row: SettingsFormTypes.IntegrationItem,
    formikProps: FormikProps<SettingsFormTypes.IntegrationSettings>
  ) => {
    const { values, setFieldValue } = formikProps;
    const appId = (row.appData as WintSuperkollAPIModel.CustomApp)?.id;

    // eslint-disable-next-line eqeqeq --- might be checking against different types
    const updatedApps = values.apps?.filter((app) => (app.appData as WintSuperkollAPIModel.CustomApp).id != appId);

    settingsService.removeCustomApp(appId).then((res) => {
      setFieldValue(FormFieldConstants.Settings.IntegrationSettings.Apps, updatedApps);
    });
  };

  const getMoreActions = (
    row: SettingsFormTypes.IntegrationItem,
    formikProps: FormikProps<SettingsFormTypes.IntegrationSettings>
  ) => {
    const actions: EditableTableMoreActionMenuItemType[] = [];
    row.appCategory === AppCategory.Default &&
      actions.push({
        description: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Settings.DeleteUser),
        icon: IconDefinitionConstants.WTrashAlt,
        highlightType: StyleEnums.DisplayType.Danger,
        onClickHandler: onCustomAppRemove,
      });

    return actions;
  };

  return loading ? (
    <Skeletons.FormSkeleton />
  ) : (
    <IntegrationSettingsForm
      defaultValues={defaultValues}
      validationSchema={validationSchema}
      ref={ref}
      {...props}
      onAddOrEditApp={onAddOrEditApp}
      appsMoreActions={getMoreActions}
    />
  );
};

export default forwardRef(IntegrationSettingsFormContainer);
